import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, ConfirmationDialog, cn, Drawer, Modal } from "@etudescarre/ui";
import { compareLotNumbers, isNil, isNotNil } from "@etudescarre/utils";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { revealOnMount } from "./contracts-panel";
import {
  describePerformanceDeletion,
  performanceMoveRefusal,
} from "./performance-deletion";
import { DragHandle } from "./yard-lots-table";

type Lot = {
  id: number;
  name: string;
  shortName: string | null;
  nLot: string | null;
};

type Performance = {
  id: number;
  lotId: number;
  name: string;
  order: number;
  interventionCount: number;
};

/** Where a dragged performance lands: before `beforeId`, or last in the lot. */
type DropTarget = { lotId: number; beforeId: number | null };

type PerformanceMove = { lotId: number; orderedIds: number[] };

const lotPerformances = (performances: Performance[], lotId: number) =>
  performances
    .filter((p) => p.lotId === lotId)
    .sort((a, b) => a.order - b.order);

/** The target lot's ids once the performance is dropped; null if nothing moves. */
export function orderAfterDrop(
  performances: Performance[],
  movedId: number,
  { lotId, beforeId }: DropTarget,
): number[] | null {
  const ids = lotPerformances(performances, lotId).map((p) => p.id);
  const from = ids.indexOf(movedId);
  if (from === -1) {
    const to = isNil(beforeId) ? ids.length : ids.indexOf(beforeId);
    return [...ids.slice(0, to), movedId, ...ids.slice(to)];
  }
  const to = isNil(beforeId) ? ids.length - 1 : ids.indexOf(beforeId);
  return from === to ? null : arrayMove(ids, from, to);
}

const dropLineClass = "shadow-[0_-2px_0_0_#3b82f6]";

function PerformanceRow({
  performance,
  position,
  acceptsDrop,
  onEdit,
  onDelete,
}: {
  performance: Performance;
  position: number;
  /** A performance dragged from another lot can land here. */
  acceptsDrop: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: performance.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "flex items-center justify-between py-1 text-sm",
        isDragging && "opacity-40",
        isOver && acceptsDrop && dropLineClass,
      )}
    >
      <span className="flex min-w-0 items-center gap-1 text-zinc-700 dark:text-zinc-300">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          label={`Déplacer la prestation ${performance.name}`}
          title={
            performance.interventionCount > 0
              ? performanceMoveRefusal(
                  performance.name,
                  performance.interventionCount,
                )
              : undefined
          }
        />
        <span className="mr-1 text-xs text-zinc-400">{position}</span>
        {performance.name}
      </span>
      <div className="flex gap-1">
        <Button plain onClick={onEdit} aria-label="Modifier">
          <PencilIcon className="size-4" />
        </Button>
        <Button plain onClick={onDelete} aria-label="Supprimer">
          <TrashIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}

/** End of a lot: dropping here puts the performance last, also in an empty lot. */
function LotEnd({
  lotId,
  acceptsDrop,
  children,
}: {
  lotId: number;
  acceptsDrop: boolean;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `lot-${lotId}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(isOver && acceptsDrop && dropLineClass)}
    >
      {children}
    </div>
  );
}

export type PerformancesDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yardId: number;
  /** Whether the yard is still in its preparation period. */
  inPreparation: boolean;
  onSuccess?: () => void;
};

export function PerformancesDrawer({
  open,
  onOpenChange,
  yardId,
  inPreparation,
  onSuccess,
}: PerformancesDrawerProps) {
  const [lots, setLots] = useState<Lot[]>([]);
  const [performances, setPerformances] = useState<Performance[]>([]);
  const [busy, setBusy] = useState(false);

  // Add form per lot
  const [addLotId, setAddLotId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");

  // Inline edit
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<Performance | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [dragged, setDragged] = useState<Performance | null>(null);
  const [overLotId, setOverLotId] = useState<number | null>(null);
  // A move waiting for confirmation, on a yard no longer in preparation.
  const [pendingMove, setPendingMove] = useState<PerformanceMove | null>(null);

  const fetchData = useCallback(async () => {
    const [lotsRes, perfRes] = await Promise.all([
      apiClient.GET("/api/admin/yards/lots", {
        params: { query: { yardId } },
      }),
      apiClient.GET("/api/admin/yards/performances", {
        params: { query: { yardId } },
      }),
    ]);
    if (lotsRes.data) {
      setLots(
        [...lotsRes.data.data.lots].sort((a, b) =>
          compareLotNumbers(a.nLot, b.nLot),
        ),
      );
    }
    if (perfRes.data) setPerformances(perfRes.data.data.performances);
  }, [yardId]);

  useEffect(() => {
    if (open) {
      fetchData();
      setAddLotId(null);
      setEditId(null);
    }
  }, [open, fetchData]);

  const refresh = async () => {
    await fetchData();
    onSuccess?.();
  };

  const createPerformance = async (lotId: number) => {
    if (!newName.trim()) {
      toaster.error({ title: "Erreur", description: "Le nom est requis" });
      return;
    }
    setBusy(true);
    const { error } = await apiClient.POST("/api/admin/yards/performances", {
      body: { yardId, lotId, name: newName.trim() },
    });
    setBusy(false);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la création",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Prestation créée" });
    setAddLotId(null);
    setNewName("");
    refresh();
  };

  const saveEdit = async (performanceId: number) => {
    if (!editName.trim()) {
      toaster.error({ title: "Erreur", description: "Le nom est requis" });
      return;
    }
    setBusy(true);
    const { error } = await apiClient.PATCH(
      "/api/admin/yards/performances/{id}",
      {
        params: { path: { id: performanceId } },
        body: { name: editName.trim() },
      },
    );
    setBusy(false);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la modification",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Prestation modifiée" });
    setEditId(null);
    refresh();
  };

  const confirmDelete = async () => {
    if (isNil(deleteTarget)) return;
    setBusy(true);
    const { error } = await apiClient.DELETE(
      "/api/admin/yards/performances/{id}",
      { params: { path: { id: deleteTarget.id } } },
    );
    setBusy(false);
    setDeleteTarget(null);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la suppression",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Prestation supprimée" });
    refresh();
  };

  const applyMove = async ({ lotId, orderedIds }: PerformanceMove) => {
    setPerformances((prev) =>
      prev.map((p) => {
        const order = orderedIds.indexOf(p.id);
        return order === -1 ? p : { ...p, lotId, order };
      }),
    );
    const { error } = await apiClient.PATCH(
      "/api/admin/yards/performances/reorder",
      { body: { lotId, orderedIds } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec du déplacement de la prestation",
      });
      fetchData();
      return;
    }
    refresh();
  };

  const dropTarget = (overId: UniqueIdentifier): DropTarget | null => {
    if (typeof overId === "string") {
      return { lotId: Number(overId.replace("lot-", "")), beforeId: null };
    }
    const over = performances.find((p) => p.id === overId);
    return isNil(over) ? null : { lotId: over.lotId, beforeId: over.id };
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setDragged(performances.find((p) => p.id === active.id) ?? null);
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    setOverLotId(isNil(over) ? null : (dropTarget(over.id)?.lotId ?? null));
  };

  const endDrag = () => {
    setDragged(null);
    setOverLotId(null);
  };

  const handleDragEnd = ({ over }: DragEndEvent) => {
    endDrag();
    const target = isNil(over) ? null : dropTarget(over.id);
    if (isNil(dragged) || isNil(target)) return;
    if (target.lotId !== dragged.lotId && dragged.interventionCount > 0) return;
    const orderedIds = orderAfterDrop(performances, dragged.id, target);
    if (isNil(orderedIds)) return;
    const move = { lotId: target.lotId, orderedIds };
    if (inPreparation) applyMove(move);
    else setPendingMove(move);
  };

  const canLeaveLot = dragged?.interventionCount === 0;
  const moveRefused =
    isNotNil(dragged) &&
    isNotNil(overLotId) &&
    overLotId !== dragged.lotId &&
    !canLeaveLot;

  const deletion = describePerformanceDeletion(
    deleteTarget?.name ?? "",
    deleteTarget?.interventionCount ?? 0,
    inPreparation,
  );

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
      // Escape cancels a keyboard drag, not the drawer.
      closeOnEscape={isNil(dragged)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Prestations</Drawer.Title>
                <Drawer.Description>
                  Gérez les prestations de chaque lot
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
              {lots.length === 0 ? (
                <p className="text-sm text-zinc-400">
                  Aucun lot. Créez d'abord des lots.
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragCancel={endDrag}
                >
                  {lots.map((lot) => {
                    const rows = lotPerformances(performances, lot.id);
                    const acceptsDrop =
                      canLeaveLot && dragged?.lotId !== lot.id;
                    return (
                      <div
                        key={lot.id}
                        className="rounded-lg border border-zinc-200 dark:border-zinc-800"
                      >
                        <div className="border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100">
                          {lot.shortName ? `${lot.shortName} — ` : ""}
                          {lot.name}
                        </div>
                        <div className="space-y-1 p-3">
                          <SortableContext
                            items={rows
                              .filter((p) => p.id !== editId)
                              .map((p) => p.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {rows.map((perf, index) =>
                              editId === perf.id ? (
                                <div
                                  key={perf.id}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) =>
                                      setEditName(e.target.value)
                                    }
                                    className="min-w-0 flex-1 rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                                  />
                                  <Button
                                    onClick={() => saveEdit(perf.id)}
                                    disabled={busy}
                                  >
                                    OK
                                  </Button>
                                  <Button plain onClick={() => setEditId(null)}>
                                    Annuler
                                  </Button>
                                </div>
                              ) : (
                                <PerformanceRow
                                  key={perf.id}
                                  performance={perf}
                                  position={index + 1}
                                  acceptsDrop={acceptsDrop}
                                  onEdit={() => {
                                    setEditId(perf.id);
                                    setEditName(perf.name);
                                  }}
                                  onDelete={() => setDeleteTarget(perf)}
                                />
                              ),
                            )}
                          </SortableContext>

                          <LotEnd lotId={lot.id} acceptsDrop={acceptsDrop}>
                            {rows.length === 0 && (
                              <p className="text-xs text-zinc-400">
                                Aucune prestation
                              </p>
                            )}
                            {addLotId === lot.id ? (
                              <div
                                ref={revealOnMount}
                                className="mt-2 flex items-center gap-2"
                              >
                                <input
                                  type="text"
                                  value={newName}
                                  onChange={(e) => setNewName(e.target.value)}
                                  placeholder="Nom de la prestation"
                                  className="min-w-0 flex-1 rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                                />
                                <Button
                                  onClick={() => createPerformance(lot.id)}
                                  disabled={busy || !newName}
                                >
                                  Ajouter
                                </Button>
                                <Button plain onClick={() => setAddLotId(null)}>
                                  Annuler
                                </Button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setAddLotId(lot.id);
                                  setNewName("");
                                }}
                                className="mt-1 flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                              >
                                <PlusIcon className="size-3.5" />
                                Ajouter une prestation
                              </button>
                            )}
                          </LotEnd>
                        </div>
                      </div>
                    );
                  })}
                  {/* A sortable row only follows the pointer within its lot. */}
                  <DragOverlay>
                    {isNotNil(dragged) && (
                      <div className="rounded-md bg-white px-3 py-1.5 text-sm text-zinc-700 shadow-md dark:bg-zinc-900 dark:text-zinc-300">
                        {dragged.name}
                      </div>
                    )}
                  </DragOverlay>
                </DndContext>
              )}
            </div>

            {moveRefused && (
              <p
                role="alert"
                className="shrink-0 border-t border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400"
              >
                {performanceMoveRefusal(
                  dragged.name,
                  dragged.interventionCount,
                )}
              </p>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>

      <ConfirmationDialog
        open={isNotNil(pendingMove)}
        title="Confirmer la modification"
        confirmLabel="Enregistrer"
        onConfirm={() => {
          if (isNotNil(pendingMove)) applyMove(pendingMove);
          setPendingMove(null);
        }}
        onCancel={() => setPendingMove(null)}
      >
        Le chantier n'est plus en période de préparation. Confirmez-vous le
        déplacement de cette prestation ?
      </ConfirmationDialog>

      <Modal.Root
        open={isNotNil(deleteTarget)}
        onOpenChange={(details) => {
          if (!details.open) setDeleteTarget(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>{deletion.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {deletion.message}
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteTarget(null)}
                  disabled={busy}
                >
                  {deletion.canDelete ? "Annuler" : "Fermer"}
                </Button>
                {deletion.canDelete && (
                  <Button color="red" onClick={confirmDelete} disabled={busy}>
                    {busy ? "Suppression..." : "Supprimer"}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </Drawer.Root>
  );
}
